import type { AxiosInstance } from "axios";
import { apiClient, internalApiClient } from "@/marketing/infrastructure/http/httpClient";
import type {
  Influencer,
  InfluencerList,
  DiscoveryFilters,
  PaginationState,
} from "@/marketing/models/influencer";
import type {
  EnrichResult,
  ListDiscoveryParams,
  ListDiscoveryResponse,
  SearchDiscoveryResponse,
} from "./utils/types";

interface APIInfluencerList {
  listId: string;
  name: string;
  description?: string;
  createdBy: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

function mapList(raw: APIInfluencerList): InfluencerList {
  return {
    id: raw.listId,
    name: raw.name,
    ...(raw.description !== undefined ? { description: raw.description } : {}),
    createdBy: raw.createdBy,
    memberCount: raw.memberCount,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

interface ListMembersResponse {
  members: (Influencer & { influencerId?: string })[];
  total: number;
  offset: number;
  limit: number;
}

function normalizeId(data: Influencer & { influencerId?: string }): Influencer {
  if (!data.id && data.influencerId) {
    data.id = data.influencerId;
  }
  return data;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface ExportProgress {
  fetched: number;
  total: number;
}

interface CampaignResponse {
  id: number;
  name: string;
  status: string;
  startAt: string;
  endAt: string;
  createdAt: string;
  updatedAt: string;
}

interface CampaignListResponse {
  campaigns: CampaignResponse[];
  total: number;
  lastEvaluatedKey?: string;
}

interface CollectionProductResponse {
  name: string;
  productLink?: string;
  productImageUrls?: string[];
}

interface CollectionProductsResponse {
  products: CollectionProductResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface CampaignBrandProduct {
  name: string;
  url: string;
  image?: string | undefined;
}

export interface CampaignBrandOption {
  id: string;
  label: string;
  products: CampaignBrandProduct[];
}

export interface CreateInfluencerParams {
  handle: string;
  platform: string;
  displayName?: string;
  bioText?: string;
  profileUrl?: string;
  followersCount?: number;
  emails?: string[];
  countrySignals?: string[];
}

export interface BulkAddProgress {
  phase: "fetching" | "adding" | "done";
  processed: number;
  total: number;
}

export class InfluencerAPIClient {
  constructor(
    private readonly client: AxiosInstance = apiClient,
    private readonly campaignClient: AxiosInstance = internalApiClient,
  ) {}

  public async searchInfluencers(
    filters: DiscoveryFilters,
    pagination: PaginationState,
  ): Promise<SearchDiscoveryResponse> {
    const params: ListDiscoveryParams = {
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      platform: filters.platform || undefined,
    };

    if (filters.search) {
      params.searchTerm = filters.search;
    }
    if (filters.minFollowers !== null) {
      params.followerMin = filters.minFollowers;
    }
    if (filters.engRateMin !== null) {
      params.engagementRateMin = filters.engRateMin;
    }
    if (filters.countryCodes.length > 0) {
      params.countryCodes = filters.countryCodes.join(",");
    }
    if (filters.languages.length > 0) {
      params.languages = filters.languages.join(",");
    }
    if (filters.hasEmail !== null) {
      params.hasEmail = filters.hasEmail;
    }

    const { data: response } = await this.client.get<ListDiscoveryResponse>(
      "/influencer/discovery",
      { params },
    );

    const totalCount = response.total;
    const pageSize = pagination.pageSize;

    return {
      data: response.results,
      totalCount,
      page: pagination.pageIndex,
      pageSize,
      totalPages: Math.ceil(totalCount / pageSize),
    };
  }

  public async getInfluencerById(id: string): Promise<Influencer> {
    const { data } = await this.client.get<
      Influencer & { influencerId?: string }
    >(`/influencer/${id}`);
    return normalizeId(data);
  }

  public async getInfluencersByIds(ids: string[]): Promise<Influencer[]> {
    if (ids.length === 0) return [];
    return Promise.all(ids.map((id) => this.getInfluencerById(id)));
  }

  public async enrichInfluencer(
    handle: string,
    platform: string = "tiktok",
  ): Promise<Influencer | null> {
    const { data } = await this.client.post<EnrichResult[]>(
      "/influencer/enrich",
      {
        handles: [handle],
        platform,
      },
    );
    const result = data[0];
    if (!result?.success || !result.influencer) return null;
    return normalizeId(
      result.influencer as Influencer & { influencerId?: string },
    );
  }

  public async selectInfluencer(
    influencerId: string,
    listId: string,
  ): Promise<void> {
    await this.client.post(`/influencer/${influencerId}/select`, { listId });
  }

  public async rejectInfluencer(influencerId: string): Promise<void> {
    await this.client.post(`/influencer/${influencerId}/reject`);
  }

  public async restoreInfluencer(influencerId: string): Promise<void> {
    await this.client.post(`/influencer/${influencerId}/restore`);
  }

  public async removeListMember(
    listId: string,
    influencerId: string,
  ): Promise<void> {
    await this.client.delete(
      `/influencer/lists/${listId}/members/${influencerId}`,
    );
  }

  public async getListMembers(
    listId: string,
    offset: number = 0,
    limit: number = 100,
  ): Promise<{ members: Influencer[]; total: number }> {
    const { data } = await this.client.get<ListMembersResponse>(
      `/influencer/lists/${listId}/members`,
      { params: { offset, limit } },
    );
    return {
      members: data.members.map((m) => normalizeId(m)),
      total: data.total,
    };
  }

  public async getAllListMembers(
    listId: string,
    onProgress?: (progress: ExportProgress) => void,
  ): Promise<{ members: Influencer[]; total: number }> {
    const PAGE_SIZE = 100;
    const CONCURRENCY = 10;
    const first = await this.getListMembers(listId, 0, PAGE_SIZE);
    if (first.total <= PAGE_SIZE) {
      onProgress?.({ fetched: first.members.length, total: first.total });
      return first;
    }

    onProgress?.({ fetched: first.members.length, total: first.total });

    const remaining = Math.ceil((first.total - PAGE_SIZE) / PAGE_SIZE);
    const pages: { members: Influencer[]; total: number }[] = [];
    let fetched = first.members.length;
    for (let batch = 0; batch < remaining; batch += CONCURRENCY) {
      if (batch > 0) await delay(50);
      const chunk = Array.from(
        { length: Math.min(CONCURRENCY, remaining - batch) },
        (_, j) =>
          this.getListMembers(listId, (batch + j + 1) * PAGE_SIZE, PAGE_SIZE),
      );
      const results = await Promise.all(chunk);
      pages.push(...results);
      fetched += results.reduce((sum, r) => sum + r.members.length, 0);
      onProgress?.({ fetched, total: first.total });
    }
    return {
      members: [first, ...pages].flatMap((p) => p.members),
      total: first.total,
    };
  }

  public async getLists(): Promise<InfluencerList[]> {
    const { data } =
      await this.client.get<APIInfluencerList[]>("/influencer/lists");
    return data.map(mapList);
  }

  public async createList(
    name: string,
    createdBy: string = "admin",
  ): Promise<InfluencerList> {
    const { data } = await this.client.post<APIInfluencerList>(
      "/influencer/lists",
      {
        name,
        createdBy,
      },
    );
    return mapList(data);
  }

  public async deleteList(listId: string): Promise<void> {
    await this.client.delete(`/influencer/lists/${listId}`);
  }

  public async bulkCreateInfluencers(
    influencers: {
      handle: string;
      platform: string;
      displayName?: string | undefined;
      emails?: string[] | undefined;
      countrySignals?: string[] | undefined;
      profileUrl?: string | undefined;
    }[],
  ): Promise<{ created: number; skipped: number; errors: string[] }> {
    const { data } = await this.client.post<{
      created: number;
      skipped: number;
      errors: string[];
    }>("/influencer/bulk", { influencers });
    return data;
  }

  public async createInfluencer(
    params: CreateInfluencerParams,
  ): Promise<Influencer> {
    const { data } = await this.client.post<
      Influencer & { influencerId?: string }
    >("/influencer", params);
    return normalizeId(data);
  }

  public async listActiveCampaigns(
    limit: number = 100,
  ): Promise<CampaignResponse[]> {
    const { data } = await this.campaignClient.get<CampaignListResponse>("/campaigns", {
      params: { status: "active", limit },
    });
    return data.campaigns;
  }

  public async listProductsByCollectionName(
    collectionName: string,
    limit: number = 12,
  ): Promise<CollectionProductResponse[]> {
    const { data } = await this.client.get<CollectionProductsResponse>(
      "/products/collection",
      {
        params: { collectionName, limit, publicationStatus: "publish" },
      },
    );
    return data.products;
  }

  public async listCampaignBrandOptions(
    limit: number = 20,
    productLimit: number = 6,
  ): Promise<CampaignBrandOption[]> {
    const activeCampaigns = await this.listActiveCampaigns(limit);
    const campaignsWithName = activeCampaigns.filter(
      (campaign) => campaign.name?.trim().length > 0,
    );

    const campaignOptions = await Promise.all(
      campaignsWithName.map(async (campaign) => {
        try {
          const products = await this.listProductsByCollectionName(
            campaign.name,
            productLimit,
          );
          const mappedProducts = products.map((product) => ({
            name: product.name,
            url: product.productLink ?? "",
            image: product.productImageUrls?.[0],
          }));
          return {
            id: campaign.name,
            label: campaign.name,
            products: mappedProducts,
          };
        } catch {
          return { id: campaign.name, label: campaign.name, products: [] };
        }
      }),
    );

    return campaignOptions;
  }

  public async removeListMembers(
    listId: string,
    influencerIds: string[],
  ): Promise<{ removed: number }> {
    const { data } = await this.client.post<{ removed: number }>(
      `/influencer/lists/${listId}/members/remove`,
      { influencerIds },
    );
    return data;
  }

  public async addListMembers(
    listId: string,
    influencerIds: string[],
    addedBy: string = "admin",
  ): Promise<{ added: number; duplicates: number }> {
    const { data } = await this.client.post<{
      added: number;
      duplicates: number;
    }>(`/influencer/lists/${listId}/members`, { influencerIds, addedBy });
    return data;
  }

  public async fetchAllFilteredIds(
    filters: DiscoveryFilters,
  ): Promise<string[]> {
    const allIds: string[] = [];
    const batchSize = 1000;
    let offset = 0;
    let total = Infinity;
    const baseParams = InfluencerAPIClient.buildDiscoveryParams(filters);

    while (offset < total) {
      const { data: response } = await this.client.get<ListDiscoveryResponse>(
        "/influencer/discovery",
        { params: { ...baseParams, offset, limit: batchSize } },
      );
      total = response.total;
      allIds.push(...response.results.map((r) => r.id));
      offset += batchSize;
      if (offset < total) await delay(50);
    }

    return allIds;
  }

  public async addAllFilteredToList(
    listId: string,
    filters: DiscoveryFilters,
    onProgress?: (progress: BulkAddProgress) => void,
  ): Promise<{ added: number; duplicates: number }> {
    onProgress?.({ phase: "fetching", processed: 0, total: 0 });
    const allIds = await this.fetchAllFilteredIds(filters);
    onProgress?.({
      phase: "fetching",
      processed: allIds.length,
      total: allIds.length,
    });

    const addBatchSize = 500;
    let totalAdded = 0;
    let totalDuplicates = 0;

    for (let i = 0; i < allIds.length; i += addBatchSize) {
      const batch = allIds.slice(i, i + addBatchSize);
      const result = await this.addListMembers(listId, batch);
      totalAdded += result.added;
      totalDuplicates += result.duplicates;
      onProgress?.({
        phase: "adding",
        processed: i + batch.length,
        total: allIds.length,
      });
    }

    onProgress?.({
      phase: "done",
      processed: allIds.length,
      total: allIds.length,
    });
    return { added: totalAdded, duplicates: totalDuplicates };
  }

  private static buildDiscoveryParams(
    filters: DiscoveryFilters,
  ): Omit<ListDiscoveryParams, "offset" | "limit"> {
    const params: Omit<ListDiscoveryParams, "offset" | "limit"> = {
      platform: filters.platform || undefined,
    };
    if (filters.search) params.searchTerm = filters.search;
    if (filters.minFollowers !== null)
      params.followerMin = filters.minFollowers;
    if (filters.engRateMin !== null)
      params.engagementRateMin = filters.engRateMin;
    if (filters.countryCodes.length > 0)
      params.countryCodes = filters.countryCodes.join(",");
    if (filters.languages.length > 0)
      params.languages = filters.languages.join(",");
    if (filters.hasEmail !== null) params.hasEmail = filters.hasEmail;
    return params;
  }
}

export const influencerAPIClient = new InfluencerAPIClient();
